import { FC, useState } from 'react';
import { ChevronDown, ChevronUp, MoveUpRight } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import {
  Degree,
  useGetCoursePlanQuery,
  useGetCoursesQuery,
  useGetDegreeCoursesQuery,
} from '@teachur/http-client';
import { isPlanComplete } from '@new/features/course-builder/lib/planView';
import { pickLastWorkedCourse } from '../../lib/courseSnapshot';
import { ContinueLearningPanel } from './ContinueLearningPanel';
import { DegreeCreditsBreakdown } from './DegreeCreditsBreakdown';

interface DegreeAccordionProps {
  degree: Pick<Degree, 'id' | 'name' | 'type' | 'shortDescription'>;
  defaultOpen: boolean;
}

const ENROLLED_COURSES_PARAMS = { page: 1, pageSize: 1000 };

export const DegreeAccordion: FC<DegreeAccordionProps> = ({ degree, defaultOpen }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const { data: degreeCourses } = useGetDegreeCoursesQuery(degree.id, { enabled: isOpen });
  const { data: coursePlan } = useGetCoursePlanQuery();
  const { data: enrolledCoursesPage } = useGetCoursesQuery(ENROLLED_COURSES_PARAMS, {
    enabled: isOpen,
    refetchOnMount: 'always',
  });
  const degreePlan = coursePlan?.plans.find((plan) => plan.degree.id === degree.id);
  const degreeCourseIds = degreePlan
    ? new Set(
        degreePlan.groups
          .flatMap((group) => group.courses)
          .map((course) => course.courseId)
          .filter((id): id is number => id != null)
      )
    : new Set((degreeCourses ?? []).map((course) => course.id));
  const degreeEnrolledCourses = (enrolledCoursesPage?.data ?? []).filter((course) =>
    degreeCourseIds.has(course.id)
  );
  const lastWorkedCourse = pickLastWorkedCourse(degreeEnrolledCourses);
  const ToggleIcon = isOpen ? ChevronUp : ChevronDown;

  if (degreePlan && isPlanComplete(degreePlan.groups)) {
    return null;
  }
  const panelId = `degree-panel-${degree.id}`;

  const onToggle = () => setIsOpen((open) => !open);
  const onOpenDegree = () => navigate({ to: `/degrees/${degree.id}` });

  return (
    <section className="overflow-hidden rounded-3xl bg-(--neutral-white-NW1000) shadow-[0_0_4px_rgba(0,0,0,0.15)] transition-all duration-300 hover:scale-[1.01] hover:shadow-lg">
      <div className="flex items-center justify-between gap-4 px-5 py-6 sm:px-7">
        <button
          type="button"
          onClick={onOpenDegree}
          className="group flex min-w-0 cursor-pointer flex-col gap-2 border-0 bg-transparent p-0 text-left"
        >
          <h2 className="m-0! flex items-center gap-2 text-lg! bold text-(--neutral-black-NB900)! transition-colors group-hover:text-(--primary-O900) sm:text-2xl!">
            {degree.name}
            <MoveUpRight className="size-5 shrink-0 text-(--neutral-black-NB700) transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </h2>
          {degree.shortDescription && (
            <p className="l4 regular m-0 line-clamp-2 text-(--neutral-black-NB700)">
              {degree.shortDescription}
            </p>
          )}
        </button>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={isOpen}
          aria-controls={panelId}
          aria-label={isOpen ? `Collapse ${degree.name}` : `Expand ${degree.name}`}
          className="flex size-8.5 shrink-0 cursor-pointer items-center justify-center rounded-full! border border-(--neutral-black-NB300) bg-transparent p-0 text-(--neutral-black-NB900) transition-colors hover:bg-(--neutral-black-NB100)"
        >
          <ToggleIcon className="size-4" />
        </button>
      </div>

      {isOpen && (
        <div id={panelId} className="border-t border-(--neutral-black-NB200)">
          <DegreeCreditsBreakdown degree={degree} />
          {lastWorkedCourse && <ContinueLearningPanel course={lastWorkedCourse} />}
        </div>
      )}
    </section>
  );
};
